import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, Zap, X } from 'lucide-react';
import {
    setApprovalHandler,
    clearApprovalHandler,
} from '../utils/videoFallbackApprovalBroker';

/**
 * VideoFallbackApprovalDialog — Feb 2026 (Phase 2)
 *
 * Global modal that pops up whenever the video-gen cascade in
 * `aiService.generateVideo` wants to fall back from a failed primary/
 * fallback model to the next model in the chain. The user must
 * explicitly approve before we spend credits on the next attempt.
 *
 * The dialog registers itself with `videoFallbackApprovalBroker` on
 * mount so any generateVideo call anywhere in the app is automatically
 * gated by this UI — no per-caller wiring needed.
 *
 * User picked answer 3a (Feb 2026): shown ONCE PER FAILURE, not once
 * per whole chain.
 */
export const VideoFallbackApprovalDialog = () => {
    const [request, setRequest] = useState(null);

    // Handler the broker calls. Must return a Promise<{approved: boolean}>.
    const handler = useCallback((payload) => {
        return new Promise((resolve) => {
            setRequest({
                payload,
                onApprove: () => { setRequest(null); resolve({ approved: true }); },
                onCancel:  () => { setRequest(null); resolve({ approved: false }); },
            });
        });
    }, []);

    useEffect(() => {
        setApprovalHandler(handler);
        return () => clearApprovalHandler();
    }, [handler]);

    if (!request) return null;

    const { payload, onApprove, onCancel } = request;
    const {
        failedModel = {},
        nextModel   = {},
        attemptNumber = 1,
        totalAttempts = 1,
        capabilityLabel = 'Video Generation',
    } = payload || {};

    const nextCredits = (nextModel?.credits != null) ? nextModel.credits : null;
    const nextTier    = nextModel?.tier || null;
    const nextName    = nextModel?.name || nextModel?.key || 'the next model';

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            data-testid="video-fallback-approval-modal"
        >
            <div className="w-full max-w-lg rounded-2xl bg-zinc-900 border border-white/10 shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-start justify-between p-5 border-b border-white/10">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
                            <AlertTriangle className="w-5 h-5 text-amber-400" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white leading-tight">Video model failed — try the next one?</h2>
                            <p className="text-xs text-zinc-400 mt-1">{capabilityLabel} · attempt {attemptNumber} of {totalAttempts}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="text-zinc-500 hover:text-white transition-colors"
                        onClick={onCancel}
                        title="Cancel"
                        data-testid="video-fallback-close-btn"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-5 space-y-4">
                    {failedModel?.key && (
                        <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3">
                            <div className="text-[11px] uppercase tracking-[0.14em] text-rose-300/80 font-semibold">Failed</div>
                            <div className="text-sm text-white mt-0.5 font-mono">{failedModel.key}</div>
                            {failedModel.error && (
                                <div className="text-xs text-rose-200/70 mt-1 line-clamp-2">{failedModel.error}</div>
                            )}
                        </div>
                    )}

                    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
                        <div className="text-[11px] uppercase tracking-[0.14em] text-emerald-300/80 font-semibold flex items-center gap-1.5">
                            <Zap className="w-3 h-3" /> Next attempt
                        </div>
                        <div className="text-sm text-white mt-0.5 font-mono" data-testid="video-fallback-next-model">{nextName}</div>
                        <div className="flex items-center gap-3 mt-2 text-xs">
                            {nextTier && (
                                <span className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-zinc-300 uppercase tracking-[0.1em]">
                                    {nextTier}
                                </span>
                            )}
                            {nextCredits != null && (
                                <span className="text-amber-300 font-semibold tabular-nums" data-testid="video-fallback-credit-cost">
                                    {nextCredits.toLocaleString()} credits
                                </span>
                            )}
                            {nextCredits == null && (
                                <span className="text-zinc-500">Credit cost not published</span>
                            )}
                        </div>
                    </div>

                    <p className="text-xs text-zinc-400 leading-relaxed">
                        Approving will charge your credits when {nextName} completes successfully. Cancel to stop the fallback chain without spending more credits.
                    </p>
                </div>

                {/* Footer */}
                <div className="flex gap-2 p-4 border-t border-white/10 bg-black/20">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="flex-1 py-2.5 rounded-xl border border-white/15 text-zinc-200 text-sm font-medium hover:bg-white/5 transition-colors"
                        data-testid="video-fallback-cancel-btn"
                    >
                        Cancel generation
                    </button>
                    <button
                        type="button"
                        onClick={onApprove}
                        className="flex-1 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold transition-colors"
                        data-testid="video-fallback-approve-btn"
                    >
                        Try {nextName.length > 20 ? 'next model' : nextName}
                    </button>
                </div>
            </div>
        </div>
    );
};
